import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppProviders from './providers/AppProviders';
import { useAuth } from './contexts/AuthContext';
import AppLayout from './components/layout/AppLayout';
import LoadingSkeleton from './components/shared/LoadingSkeleton';
import ErrorBoundary from './components/shared/ErrorBoundary';
import DEFERRED_SURFACES from './config/deferredSurfaces';
import { ConsentTabQueryRedirect } from './modules/consent/hooks/useConsentTabs';

/**
 * W1 — Tab redirect for /assessment-center/:id/<tab> deep links.
 *
 * Spec §10.2.5 / §10.2.6 calls for direct links to the Versions and Approval
 * tabs. AssessmentRecordPage exposes them via ?tab= search-param convention,
 * so this thin redirect preserves both URL shapes.
 */
export function AssessmentTabRedirect({ tab }) {
  const { id } = useParams();
  return <Navigate to={`/assessment-center/${id}?tab=${tab}`} replace />;
}

// Lazy-loaded module pages
const LoginPage = lazy(() => import('./modules/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./modules/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./modules/auth/ResetPasswordPage'));
const EmailChangeConfirmPage = lazy(() => import('./modules/auth/EmailChangeConfirmPage'));
const DashboardPage = lazy(() => import('./modules/dashboard/DashboardPage'));
const UsersPage = lazy(() => import('./modules/users/UsersPage'));
const AuditPage = lazy(() => import('./modules/audit/AuditPage'));
const SystemHealthPage = lazy(() => import('./modules/admin/SystemHealthPage'));
const EngineConfigOverridePage = lazy(() => import('./modules/admin/EngineConfigOverridePage'));
const SettingsPage = lazy(() => import('./modules/settings/SettingsPage'));
const ReportsPage = lazy(() => import('./modules/reports/ReportsPage'));
const ScannerPage = lazy(() => import('./modules/scanner/ScannerPage'));
const ScannerCommandCenter = lazy(() => import('./modules/scanner/components/ScannerCommandCenter'));
const PDPLAssessmentPage = lazy(() => import('./modules/pdpl-assessment/PDPLAssessmentPage'));
const DPOModulePage = lazy(() => import('./modules/dpo/DPOModulePage'));
const ROPAPage = lazy(() => import('./modules/ropa/ROPAPage'));
const ROPARecordPage = lazy(() => import('./modules/ropa/pages/ROPARecordPage'));
const EvidenceVerificationPage = lazy(() => import('./modules/ropa/pages/EvidenceVerificationPage'));
const ROPACommandCenter = lazy(() => import('./modules/ropa/ROPACommandCenter'));
const DPOCurationQueuePage = lazy(() => import('./modules/ropa/pages/DPOCurationQueuePage'));
const ROPAImportWizardPage = lazy(() => import('./modules/ropa/pages/ROPAImportWizardPage'));
const PurposeCurationCanvasPage = lazy(() => import('./modules/ropa/pages/PurposeCurationCanvasPage'));
const ConsentPage = lazy(() => import('./modules/consent/ConsentPage'));
const ConsentIdentityQueuePage = lazy(() => import('./modules/consent/ConsentIdentityQueuePage'));
const ConsentPurposeCurationPage = lazy(() => import('./modules/consent/ConsentPurposeCurationPage'));
const BannerConfiguratorWizard = lazy(() => import('./modules/consent/components/BannerConfiguratorWizard'));
const DSRPage = lazy(() => import('./modules/dsr/DSRPage'));
const DSRDetailPage = lazy(() => import('./modules/dsr/pages/DSRDetailPage'));
const DSRInboundAPIKeyManager = lazy(() => import('./modules/dsr/DSRInboundAPIKeyManager'));
const SubjectLinkLandingPage = lazy(() => import('./modules/dsr/pages/SubjectLinkLandingPage'));
const BreachPage = lazy(() => import('./modules/breach/BreachPage'));
const NotificationPage = lazy(() => import('./modules/breach-notifications/NotificationPage'));
const AssessmentCenterPage = lazy(() => import('./modules/assessment-center/AssessmentCenterPage'));
const AssessmentRecordPage = lazy(() => import('./modules/assessment-center/pages/AssessmentRecordPage'));
const RiskPage = lazy(() => import('./modules/risk/RiskPage'));
const DataFlowPage = lazy(() => import('./modules/dataflow/DataFlowPage'));
const PolicyManagementPage = lazy(() => import('./modules/policy-management/PolicyManagementPage'));
const DPTAListPage = lazy(() => import('./modules/dpta/pages/DPTAListPage'));
const DPTADetailPage = lazy(() => import('./modules/dpta/pages/DPTADetailPage'));
const DPTAQuestionnairePage = lazy(() => import('./modules/dpta/pages/DPTAQuestionnairePage'));
const AdequacyRegistryPage = lazy(() => import('./modules/dpta/pages/AdequacyRegistryPage'));
const AuditLogPage = lazy(() => import('./modules/policy-management/AuditLogPage'));
const ClassificationPage = lazy(() => import('./modules/classification/ClassificationPage'));
const DataDiscoveryPage = lazy(() => import('./modules/discovery/DataDiscoveryPage'));
const MinimizationPage = lazy(() => import('./modules/minimization/MinimizationPage'));
const OrganizationPage = lazy(() => import('./modules/organization/OrganizationPage'));
const PrivilegeReviewPage = lazy(() => import('./modules/privilege-review/PrivilegeReviewPage'));
const ReviewCenterPage = lazy(() => import('./modules/review-center/ReviewCenterPage'));
const DPAListPage = lazy(() => import('./modules/dpa/DPAListPage'));
const PrivacyNoticeListPage = lazy(() => import('./modules/privacy-notices/PrivacyNoticeListPage'));
const ProfilePage = lazy(() => import('./modules/profile/ProfilePage'));
const IdentityProviderConfigPage = lazy(() => import('./modules/settings/IdentityProviderConfigPage'));
const ApproverConfigPage = lazy(() => import('./modules/settings/ApproverConfigPage'));
const CycleConfigPage = lazy(() => import('./modules/settings/CycleConfigPage'));

function AuthLoading() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center h-screen bg-slate-50">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-slate-600 text-sm">{t('loading')}</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <AuthLoading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function GuardedRoute({ permission, variant = 'table', children }) {
  const { hasPermission } = useAuth();
  // Spec 052 T250 (FR-029, FR-060) — `permission` may be a single string OR an
  // array of strings with ANY-OF semantics: the route opens when the viewer
  // holds at least one of the listed permissions (live-resolution RBAC,
  // Constitution XVI). A single string keeps the original single-permission
  // gate unchanged for every existing route.
  const allowed = Array.isArray(permission)
    ? permission.some((p) => hasPermission(p))
    : hasPermission(permission);
  if (!allowed) return <Navigate to="/dashboard" replace />;
  return (
    <SuspenseRoute variant={variant}>
      {children}
    </SuspenseRoute>
  );
}

function PublicRoute({ children }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <AuthLoading />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

/**
 * Wrapper that provides a route-type-specific Suspense fallback.
 * - "form"  → form skeleton (DPIA, TRA, Assessment, PDPL Assessment)
 * - "cards" → card grid skeleton (Dashboard)
 * - "table" → table skeleton (all list / default pages)
 */
function SuspenseRoute({ variant = 'table', children }) {
  return (
    <Suspense fallback={<LoadingSkeleton variant={variant} />}>
      <ErrorBoundary>{children}</ErrorBoundary>
    </Suspense>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<SuspenseRoute><PublicRoute><LoginPage /></PublicRoute></SuspenseRoute>} />

      {/* M-FE-1 — Public password recovery flow (H-SEC-3 backend) */}
      <Route path="/forgot-password" element={<SuspenseRoute><PublicRoute><ForgotPasswordPage /></PublicRoute></SuspenseRoute>} />
      <Route path="/reset-password" element={<SuspenseRoute><ResetPasswordPage /></SuspenseRoute>} />
      {/* Public — confirms an email-change token. The user may already be logged
          in (from a different session) or not; either way the token is the
          proof of control of the new address. M-SELF-3. */}
      <Route path="/email-change/confirm" element={<SuspenseRoute><EmailChangeConfirmPage /></SuspenseRoute>} />

      {/* T49 — Public DSR one-time-link landings. Registered BEFORE the
          authenticated route tree so they bypass JWT / AuthShell entirely.
          Identity proof is the magic-code 2FA on the page itself. Spec §5.6, §5.7. */}
      {/* /dsr/link/:token is RETIRED (D22). The owner one-time link and its
          public routes are deleted; an owner answers through their system's
          key or via the DPO. The SUBJECT link route below survives. */}
      <Route path="/dsr/subject-link/:token" element={<SuspenseRoute><SubjectLinkLandingPage /></SuspenseRoute>} />

      {/* All authenticated routes use AppLayout (Sidebar + TopBar + Outlet) */}
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        {/* Dashboard */}
        <Route path="/dashboard" element={<GuardedRoute permission="view_dashboard" variant="cards"><DashboardPage /></GuardedRoute>} />

        {/* Form pages */}
        <Route path="/assessment-center" element={<GuardedRoute permission="view_compliance" variant="cards"><AssessmentCenterPage /></GuardedRoute>} />
        <Route path="/assessment-center/:id" element={<GuardedRoute permission="view_compliance" variant="cards"><AssessmentRecordPage /></GuardedRoute>} />
        {/* W1 deep links — preserve historical /:id/<tab> URLs by redirecting
            into the canonical AssessmentRecordPage tab via ?tab= search param.
            Spec §10.2.5 + §10.2.6: /versions and /approve must round-trip. */}
        <Route path="/assessment-center/:id/versions" element={<GuardedRoute permission="view_compliance" variant="cards"><AssessmentTabRedirect tab="versions" /></GuardedRoute>} />
        <Route path="/assessment-center/:id/approve" element={<GuardedRoute permission="view_compliance" variant="cards"><AssessmentTabRedirect tab="approval" /></GuardedRoute>} />
        <Route path="/pdpl-assessment" element={<GuardedRoute permission="view_compliance" variant="form"><PDPLAssessmentPage /></GuardedRoute>} />

        {/* M-FE-5 — Self-service profile (H-SELF-1 + H-SELF-3 backend).
            No permission gate — every authenticated user reaches their own profile. */}
        <Route path="/profile" element={<SuspenseRoute><ProfilePage /></SuspenseRoute>} />

        {/* List pages */}
        <Route path="/breaches" element={<GuardedRoute permission="view_breaches"><BreachPage /></GuardedRoute>} />
        <Route path="/dsr" element={<GuardedRoute permission="view_dsr"><DSRPage /></GuardedRoute>} />
        <Route path="/dsr/:id" element={<GuardedRoute permission="view_dsr"><DSRDetailPage /></GuardedRoute>} />
        <Route path="/dsr/inbound-api-keys" element={<GuardedRoute permission="dsr_inbound_api_admin"><DSRInboundAPIKeyManager canManageInboundKeys /></GuardedRoute>} />
        <Route path="/users" element={<GuardedRoute permission="view_users"><UsersPage /></GuardedRoute>} />
        {/* 053 tail-lane fix — `?tab=<id>` deep links onto the bare /consent
            path (every DPOTask action_url of this shape) are redirected to
            the named tab's OWN guarded route BEFORE ConsentPage mounts, via
            ConsentTabQueryRedirect (see useConsentTabs.js for why: reading
            the param inside a ConsentPage-mounted effect races
            useURLFilters's own URL-syncing effect and loses). */}
        <Route path="/consent" element={<GuardedRoute permission="view_consent"><ConsentTabQueryRedirect><ConsentPage /></ConsentTabQueryRedirect></GuardedRoute>} />
        {/* The Cookie Consent (websites) tab's own address. It was the ONE tab
            with no route, so `handleTabChange('websites')` fell through to the
            module root — whose pathname reading is 'records' — and the click
            was overwritten a frame later: the owner's "I click on a tab and
            find myself in another tab". Gated view_consent, exactly the gate
            this tab already rendered under at the bare root; giving a tab an
            address must not move it to a different permission. */}
        <Route path="/consent/websites" element={<GuardedRoute permission="view_consent"><ConsentPage /></GuardedRoute>} />
        <Route path="/consent/command-center" element={<GuardedRoute permission="view_consent"><ConsentPage /></GuardedRoute>} />
        {/* 053 T098 (US1) — the purpose-grain requirement register. Renders
            the ConsentPage shell with the Requirements tab active; gated the
            same as the module root (view_consent — every register-row read
            route is view_consent, routes_register.py). */}
        <Route path="/consent/requirements" element={<GuardedRoute permission="view_consent"><ConsentPage /></GuardedRoute>} />
        {/* 053 T139 (US3) — the Evidence tab: attestations of how consent is
            collected. Deep-linkable by `?purpose_id=`, which is exactly the
            `action_url` T141's chase task carries, so an overdue or never-
            attested register row in the DPO inbox opens the row it names.
            Gated view_consent (the attestation READ routes are view_consent;
            recording one additionally needs manage_consent, enforced by the
            route and hinted by a disabled control). */}
        <Route path="/consent/evidence" element={<GuardedRoute permission="view_consent"><ConsentPage /></GuardedRoute>} />
        {/* DR-05fe — purpose-curation queue (legacy free-text consent → processing
            purpose linking). Renders the ConsentPage shell with the Curation tab
            active. Gated on view_compliance to match the queue endpoint
            (routes.py:1465). Distinct from /ropa/purpose-curation, which curates
            ROPA purpose *proposals* (a different workflow + endpoint). */}
        <Route path="/consent/purpose-curation" element={<GuardedRoute permission="view_compliance"><ConsentPurposeCurationPage /></GuardedRoute>} />
        <Route path="/consent/banner/configure" element={<GuardedRoute permission="scan_websites" variant="form"><BannerConfiguratorWizard /></GuardedRoute>} />
        {/* DR-24 — the Import tab is gated on manage_compliance (the import
            endpoint's permission); align the deep-link route guard to match so
            a user can't reach a tab the shell would hide for them. */}
        <Route path="/consent/import" element={<GuardedRoute permission="manage_compliance"><ConsentPage /></GuardedRoute>} />
        {/* Spec 044 US3 — DPO consent-identity queue. dpo_oversight gates
            visibility (Super Admin + Admin observer + DPO); action buttons
            inside the slide-over additionally check manage_consent_identity
            (Super Admin + DPO only — Admin is VIEW-only). */}
        <Route path="/consent/identity-queue" element={<GuardedRoute permission="dpo_oversight"><ConsentIdentityQueuePage /></GuardedRoute>} />
        <Route path="/ropa" element={<GuardedRoute permission="view_ropa"><ROPAPage /></GuardedRoute>} />
        <Route path="/ropa/records/:id" element={<GuardedRoute permission="view_ropa"><ROPARecordPage /></GuardedRoute>} />
        {/* Spec 052 US3 (FR-024) — evidence-verification trace surface. The
            "Verify with SDAIA inspector" CTA on a ROPA record lands here; shows
            the per-column classifications + the exact engine run that produced
            them. Read gate matches the ROPA record page (view_ropa). */}
        <Route path="/ropa/evidence/:evidenceId" element={<GuardedRoute permission="view_ropa"><EvidenceVerificationPage /></GuardedRoute>} />
        <Route path="/ropa/command-center" element={<GuardedRoute permission="view_ropa"><ROPACommandCenter /></GuardedRoute>} />
        {/* Spec 024 — DPO purpose curation queue. Spec 052 T250 (FR-029/FR-060):
            ANY-OF gate — dpo_oversight (DPO + Admin per init_db.py) OR the
            read-only view_purpose_curation seed (dc052_permission_seeds). Action
            buttons inside the page additionally check curate_purpose_proposals. */}
        <Route path="/ropa/purpose-curation" element={<GuardedRoute permission={['dpo_oversight', 'view_purpose_curation']}><DPOCurationQueuePage /></GuardedRoute>} />
        {/* Spec 030 US6 — ROPA reverse import wizard. Requires manage_ropa
            (same gate as creating/editing ROPA records). */}
        <Route path="/ropa/import" element={<GuardedRoute permission="manage_ropa"><ROPAImportWizardPage /></GuardedRoute>} />
        {/* Spec 030 US7 — Visual Canvas (P3 stretch). Same permission as curation queue. */}
        <Route path="/ropa/purpose-curation/visual" element={<GuardedRoute permission="curate_purpose_proposals"><PurposeCurationCanvasPage /></GuardedRoute>} />

        {/* Module 023 — DPIA Threshold Assessment (DPTA). view_dpta permission
            grants read of the verdict + history; manage_dpta is enforced
            server-side on the recompute / override actions. */}
        {/* Spec 031 D5 — the threshold verdict is anchored on the register
            entry (ROPARecord), so the DPTA detail route is keyed on the ROPA. */}
        <Route path="/dpta" element={<GuardedRoute permission="view_dpta"><DPTAListPage /></GuardedRoute>} />
        <Route path="/dpta/ropas/:ropaId" element={<GuardedRoute permission="view_dpta"><DPTADetailPage /></GuardedRoute>} />
        {/* The DPO questionnaire — declaration write path — is keyed on the ROPA. */}
        <Route path="/dpta/ropas/:ropaId/questionnaire" element={<GuardedRoute permission="manage_dpta"><DPTAQuestionnairePage /></GuardedRoute>} />
        {/* Phase C1 — governed Adequacy Registry (canonical source). Tab in the
            DPTA hub; view gated on view_dpta, write actions on manage_regulatory_registry. */}
        <Route path="/dpta/registry" element={<GuardedRoute permission="view_dpta"><AdequacyRegistryPage /></GuardedRoute>} />
        {/* GH #165 — Risk Register module deferred; flip
            DEFERRED_SURFACES.SHOW_RISK_REGISTER to restore. Unrouted deep
            links fall through to the path="*" catch-all below. */}
        {DEFERRED_SURFACES.SHOW_RISK_REGISTER && (
          <Route path="/risk" element={<GuardedRoute permission="view_risks"><RiskPage /></GuardedRoute>} />
        )}
        <Route path="/audit" element={<GuardedRoute permission="view_audit_logs"><AuditPage /></GuardedRoute>} />
        <Route path="/audit/dpo" element={<Navigate to="/audit" replace />} />
        <Route path="/admin/system-health" element={<GuardedRoute permission="view_audit_logs"><SystemHealthPage /></GuardedRoute>} />
        <Route path="/admin/clustering-config" element={<GuardedRoute permission="manage_engine_config"><EngineConfigOverridePage /></GuardedRoute>} />
        <Route path="/scanner" element={<GuardedRoute permission="scan_websites"><ScannerPage /></GuardedRoute>} />
        <Route path="/scanner/command-center" element={<GuardedRoute permission="scan_websites"><ScannerCommandCenter /></GuardedRoute>} />
        {/* GH #166 — Breach Notifications module deferred; flip
            DEFERRED_SURFACES.SHOW_BREACH_NOTIFICATIONS to restore. Unrouted
            deep links fall through to the path="*" catch-all below. */}
        {DEFERRED_SURFACES.SHOW_BREACH_NOTIFICATIONS && (
          <Route path="/breach-notifications" element={<GuardedRoute permission="notify_breach"><NotificationPage /></GuardedRoute>} />
        )}
        {/* DPO module — single unified 6-tab page. Legacy sub-routes redirect. */}
        <Route path="/dpo" element={<GuardedRoute permission="dpo_oversight"><DPOModulePage /></GuardedRoute>} />
        <Route path="/dpo/command-center" element={<Navigate to="/dpo" replace />} />
        <Route path="/dpo/management" element={<Navigate to="/dpo?tab=management" replace />} />
        <Route path="/dpo/profile" element={<Navigate to="/dpo?tab=profile" replace />} />
        <Route path="/dpo/inspector-dossier" element={<Navigate to="/dpo?tab=inspector" replace />} />

        {/* Other pages */}
        {/* GH #164 — Policy Management module deferred; flip
            DEFERRED_SURFACES.SHOW_POLICY_MANAGEMENT to restore the main
            route + all 5 legacy redirects below. Unrouted deep links
            (including bare /compliance) fall through to the path="*"
            catch-all at the bottom of this tree. */}
        {DEFERRED_SURFACES.SHOW_POLICY_MANAGEMENT && (
          <>
            <Route path="/policy-management" element={<GuardedRoute permission="view_compliance"><PolicyManagementPage /></GuardedRoute>} />
            <Route path="/compliance" element={<Navigate to="/policy-management?tab=policies" replace />} />
            <Route path="/policy-management/command-center" element={<Navigate to="/policy-management?tab=command-center" replace />} />
            <Route path="/policy-management/multi-framework" element={<Navigate to="/policy-management?tab=frameworks" replace />} />
            <Route path="/policy-management/ai-governance" element={<Navigate to="/policy-management?tab=ai-governance" replace />} />
            <Route path="/policy-management/purpose-limitation" element={<Navigate to="/policy-management?tab=purpose-limitation" replace />} />
          </>
        )}
        {/* Gate at route uses view_compliance so unauthorized users reach the page
            and see the in-page "Insufficient permission" callout (T131 AC-17).
            The AuditLogPage component now lives under policy-management/ after
            the 020 module rename; the /compliance/audit-log URL is preserved
            for backward-compatible deep links. */}
        <Route path="/compliance/audit-log" element={<GuardedRoute permission="view_compliance"><AuditLogPage /></GuardedRoute>} />
        <Route path="/classification/*" element={<GuardedRoute permission="view_classifications"><ClassificationPage /></GuardedRoute>} />
        <Route path="/discovery" element={<GuardedRoute permission="view_data_sources"><DataDiscoveryPage /></GuardedRoute>} />
        <Route path="/minimization" element={<GuardedRoute permission="view_data_sources"><MinimizationPage /></GuardedRoute>} />
        <Route path="/dataflow" element={<GuardedRoute permission="view_data_flows"><DataFlowPage /></GuardedRoute>} />
        {/* GH #167 — Reports module deferred; flip
            DEFERRED_SURFACES.SHOW_REPORTS to restore. Unrouted deep links
            fall through to the path="*" catch-all below. */}
        {DEFERRED_SURFACES.SHOW_REPORTS && (
          <Route path="/reports" element={<GuardedRoute permission="view_reports"><ReportsPage /></GuardedRoute>} />
        )}
        <Route path="/settings" element={<GuardedRoute permission="view_settings"><SettingsPage /></GuardedRoute>} />
        <Route path="/settings/identity-providers" element={<GuardedRoute permission="manage_settings"><IdentityProviderConfigPage /></GuardedRoute>} />
        <Route path="/settings/approver-config" element={<GuardedRoute permission="manage_settings"><ApproverConfigPage /></GuardedRoute>} />
        <Route path="/settings/cycle-config" element={<GuardedRoute permission="manage_settings"><CycleConfigPage /></GuardedRoute>} />
        <Route path="/organization" element={<GuardedRoute permission="manage_settings" variant="form"><OrganizationPage /></GuardedRoute>} />
        <Route path="/privilege-reviews" element={<GuardedRoute permission="view_privilege_reviews"><PrivilegeReviewPage /></GuardedRoute>} />
        <Route path="/review-center/*" element={<GuardedRoute permission="view_classifications"><ReviewCenterPage /></GuardedRoute>} />
        {/* Spec 028 P4 — DPA module (FR-028-05) */}
        <Route path="/dpa" element={<GuardedRoute permission="view_ropa"><DPAListPage /></GuardedRoute>} />
        <Route path="/dpa/new" element={<GuardedRoute permission="view_ropa"><DPAListPage /></GuardedRoute>} />
        {/* Spec 028 P5 — PrivacyNotice module (FR-028-07) */}
        <Route path="/privacy-notices" element={<GuardedRoute permission="view_ropa"><PrivacyNoticeListPage /></GuardedRoute>} />

        {/* Default: redirect root to dashboard */}
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AppProviders>
      <AppRoutes />
    </AppProviders>
  );
}

export default App;
